import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArtistPortalConnectFailure, ArtistPortalConnectResult } from 'fym-shared/src/smartlinks/artistPortals';
import { useCompleteArtistPortalAuthMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { forgetPortalReturn, portalReturnAccount } from './portalReturnSession';
import { portalReturnFailure, portalReturnTarget } from './portalReturnOutcome';

/**
 * The work of the return page: `auth/complete` one time with the `state` of the address, then the
 * Catalog screen with the outcome in the query. The account is the one that started the sign-in in
 * this tab, else the active artist.
 */
export function usePortalReturn(integrationType: string, state: string): void {
  const activeAccountId = useAccountId();
  const accountId = portalReturnAccount(state) ?? activeAccountId;
  const [complete] = useCompleteArtistPortalAuthMutation();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current || accountId === null || accountId === undefined) {
      return;
    }
    started.current = true;
    const go = (target: string): void => {
      Promise.resolve(navigate(target, { replace: true })).catch(console.error);
    };
    complete({ accountId, integrationType, body: { sessionUuid: state, redirectUrl: window.location.href } })
      .then((answer) => {
        forgetPortalReturn();
        if (!isSuccessfulResult(answer)) {
          go(
            portalReturnTarget({
              accountId,
              integrationType,
              result: ArtistPortalConnectResult.error,
              reason: portalReturnFailure(answer.error),
            })
          );
          return;
        }
        const hasArtist = answer.data.connection.artist !== null || answer.data.artists.length === 1;
        go(
          portalReturnTarget({
            accountId,
            integrationType,
            result: hasArtist ? ArtistPortalConnectResult.connected : ArtistPortalConnectResult.pickArtist,
          })
        );
      })
      .catch(() => {
        go(
          portalReturnTarget({
            accountId,
            integrationType,
            result: ArtistPortalConnectResult.error,
            reason: ArtistPortalConnectFailure.unknown,
          })
        );
      });
  }, [accountId, complete, integrationType, navigate, state]);
}
